import ListFormItem from "@arelia-components/form/ListFormItem";
import { Subscription, SubscriptionPlan, SubscriptionPlanName } from "@arelia-types/subscriptions";
import { formatDate } from "@arelia-utils/helpers/dates";
import { List, Button, Box, Typography } from "@mui/material";
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import { useSearchParams } from "react-router-dom";
import { subDays } from "date-fns";
import { useState } from "react";
import { formatCurrency } from "../../../../utils/helpers/formatCurrency";
import SubscriptionInfoActionsUser from "./SubscriptionInfoActionsUser";
import PurchaseMessagesDialog from "./PurchaseMessagesDialog";
import { useTwilioConfigAPI, useBrandRegistrationStatusAPI } from "../../../../features/fans/settings/settings.api";
import ChangePlanDialog from "./ChangePlanDialog";
import { displayPlanName } from "./Helper";
import { toast } from "sonner";

type Props = {
  subscription: Subscription;
  changePlanAvailable: boolean;
};

export default function SubscriptionInfoUser({ subscription, changePlanAvailable }: Props) {
  const nextBillingDate = subscription.nextBillingDate ?
    (subscription.plan === SubscriptionPlanName.Starter ?
      subDays(new Date(subscription.nextBillingDate), 1) :
      new Date(subscription.nextBillingDate)) :
    null;

  const [searchParams, setSearchParams] = useSearchParams();
  const changePlanParam = searchParams.get('changePlan');
  const [openChangePlanDialog, setOpenChangePlanDialog] = useState(!!changePlanParam);

  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);

  const { data: registrationStatus } = useBrandRegistrationStatusAPI(true);
  const { data: twilioConfig } = useTwilioConfigAPI();

  const showSmsFeatures = registrationStatus?.completed === true && subscription.plan === SubscriptionPlanName.PremiumMyFans;

  const handleOpenSmsModal = () => setPurchaseModalOpen(true);
  const handleCloseSmsModal = () => setPurchaseModalOpen(false);

  const handleOpenChangePlan = () => setOpenChangePlanDialog(true);
  const handleCloseChangePlan = () => {
    setOpenChangePlanDialog(false);
    if (changePlanParam) {
      searchParams.delete('changePlan');
      setSearchParams(searchParams);
    }
  };

  const onChangePlanSuccess = () => {
    handleCloseChangePlan();
    toast.success("Plan changed successfully");
  };

  return (
    <Box sx={styles.listWrapper}>
      <List sx={styles.list}>
        <ListFormItem label="Plan">
          <Box sx={styles.planRow}>
            <Typography>{displayPlanName(subscription.plan, subscription.isFreePlan)}</Typography>
            {changePlanAvailable && (
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={handleOpenChangePlan}
                startIcon={<PublishedWithChangesIcon />}
                sx={styles.changePlanButton}
              >
                Change Plan
              </Button>
            )}
          </Box>
        </ListFormItem>

        <ListFormItem label="Payment">
          <Typography sx={styles.paymentText}>
            <strong>{formatCurrency(subscription.monthlyPayment)}</strong> per month
          </Typography>
        </ListFormItem>

        {nextBillingDate && (
          <ListFormItem
            label={subscription.plan === SubscriptionPlanName.Premium ? "Next Billing Date" : "End Date"}
          >
            <Typography>{formatDate(nextBillingDate, "MM/dd/yyyy")}</Typography>
          </ListFormItem>
        )}

        <ListFormItem label="Status">
          <Typography>{subscription.active ? 'Active' : 'Inactive'}</Typography>
        </ListFormItem>

        {showSmsFeatures && (
          <>
            <ListFormItem label="SMS Messages">
              <Typography>
                {twilioConfig?.messageLimitCurrent?.toLocaleString() ?? 0} per month
              </Typography>
            </ListFormItem>

            <ListFormItem label="Additional SMS Messages">
              <Box sx={styles.additionalMessagesBox}>
                <Typography sx={styles.messageCount}>
                  +{twilioConfig?.additionalMessageLimit?.toLocaleString() ?? 0} per month
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenSmsModal}
                  sx={styles.buyMessagesButton}
                >
                  Buy More Messages
                </Button>
              </Box>
            </ListFormItem>
          </>
        )}
      </List>

      <SubscriptionInfoActionsUser subscription={subscription} />

      {purchaseModalOpen && (
        <PurchaseMessagesDialog onClose={handleCloseSmsModal} />
      )}

      {openChangePlanDialog && (
        <ChangePlanDialog
          onClose={handleCloseChangePlan}
          onSuccess={onChangePlanSuccess}
          plan={subscription.plan}
          isFreePlan={subscription.isFreePlan}
        />
      )}
    </Box>
  );
}

const styles = {
  listWrapper: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "20px"
  },
  list: {
    width: "100%"
  },
  additionalMessagesBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%"
  },
  buyMessagesButton: {
    minWidth: "150px"
  },
  changePlanButton: {
    minWidth: "120px"
  },
  messageCount: {
    display: "flex",
    alignItems: "center"
  },
  paymentText: {
    fontWeight: 400
  },
  planRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%"
  }
};
